import { useState, useMemo, useRef, useEffect } from "react";
import { BsArrowDownUp } from "react-icons/bs";
import { ToastContainer, toast } from "react-toastify";
import Draggable from "react-draggable";
import "react-toastify/dist/ReactToastify.css";
import "./ActivityTable.css";

const API_URL = process.env.REACT_APP_API_URL;

const allowedFields = [
  "id",
  "taskid",
  "projectid",
  "project_name",
  "userid",
  "user_name",
  "activity",
  "seq_no",
  "wordcounts",
  "status",
  "quality_grade",
  "taskmanager",
  "penalty",
  "payment_status",
  "lastmodifiedate",
  "recommendation",
];

const Table = ({
  currentPosts,
  setCurrentPosts,
  handleCheckboxChange,
  selectedIds,
  selectAll,
  handleSelectAll,
}) => {
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [qualityGrade, setQualityGrade] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [penalty, setPenalty] = useState("");

  const [projects, setProjects] = useState([]);
  const [usersLogin, setUserLogins] = useState([]);

  // Fetch projects and users login on mount
  useEffect(() => {
    fetch(`${API_URL}/projects`)
      .then((res) => res.json())
      .then(setProjects)
      .catch((err) => console.error("Error fetching projects:", err));

    fetch(`${API_URL}/userAuthLogin`)
      .then((res) => res.json())
      .then(setUserLogins)
      .catch((err) => console.error("Error fetching userLogin:", err));
  }, []);

  const projectMap = useMemo(
    () => Object.fromEntries(projects.map((p) => [p.id, p.projectname])),
    [projects]
  );
  const userMap = useMemo(
    () =>
      Object.fromEntries(
        usersLogin.map((u) => [u.id, u.name || u.username || u.full_name])
      ),
    [usersLogin]
  );

  // Get logged in user from localStorage
  const userObjectArray = JSON.parse(localStorage.getItem("userObjectArray")) || [];
  const loggedInUser = userObjectArray[0] || {};
  const taskManagerName = loggedInUser?.name || "";

  const selectedTask =
    currentPosts.find((row) => row.taskid === selectedTaskId) || null;

  // Whenever selectedTask changes, initialize form states
  useEffect(() => {
    if (selectedTask) {
      setQualityGrade(selectedTask.quality_grade ?? "");
      setRecommendation(selectedTask.recommendation ?? "");
      setPenalty(selectedTask.penalty ?? "");
      setEditMode(false);
    } else {
      setQualityGrade("");
      setRecommendation("");
      setPenalty("");
      setEditMode(false);
    }
  }, [selectedTask]);

  const [colWidths, setColWidths] = useState({
    id: 50,
    taskid: 120,
    seq_no: 40,
    projectid: 100,
    userid: 130,
    status: 80,
    activity: 80,
    quality_grade: 70,
    penalty: 60,
  });

  const tableRef = useRef(null);
  const startX = useRef(0);
  const startWidth = useRef(0);
  const resizingCol = useRef(null);

  const onMouseDown = (e, colKey) => {
    startX.current = e.clientX;
    resizingCol.current = colKey;
    startWidth.current = colWidths[colKey] ?? 50;
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e) => {
    if (!resizingCol.current) return;
    const deltaX = e.clientX - startX.current;
    setColWidths((prev) => ({
      ...prev,
      [resizingCol.current]: Math.max((startWidth.current || 40) + deltaX, 40),
    }));
  };

  const onMouseUp = () => {
    resizingCol.current = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };

  const sortedData = useMemo(() => {
    const arr = [...currentPosts];
    if (sortColumn !== "") {
      arr.sort((a, b) => {
        let va = a[sortColumn];
        let vb = b[sortColumn];
        if (sortColumn === "lastmodifiedate") {
          const da = new Date(va).getTime();
          const db = new Date(vb).getTime();
          return sortDirection === "asc" ? da - db : db - da;
        } else {
          va = va ?? "";
          vb = vb ?? "";
          if (typeof va === "string" && typeof vb === "string") {
            return sortDirection === "asc"
              ? va.localeCompare(vb)
              : vb.localeCompare(va);
          }
          if (va < vb) return sortDirection === "asc" ? -1 : 1;
          if (va > vb) return sortDirection === "asc" ? 1 : -1;
          return 0;
        }
      });
    }
    return arr;
  }, [currentPosts, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (column === sortColumn) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleUpdate = async () => {
    if (!selectedTaskId) {
      toast.error("Invalid Task ID");
      return;
    }

    const gradeNum = parseInt(qualityGrade, 10);
    if (isNaN(gradeNum)) {
      toast.warn("Please select a valid Quality Grade");
      return;
    }

    // Optionally, you can check penalty is a valid number, e.g.:
    const penaltyNum = parseFloat(penalty);
    if (penalty !== "" && isNaN(penaltyNum)) {
      toast.warn("Please enter a valid penalty number");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/activity/${selectedTaskId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": loggedInUser.id || "",  // Authentication header
        },
        body: JSON.stringify({
          quality_grade: gradeNum,
          taskmanager: taskManagerName,
          recommendation,
          penalty: penaltyNum,  // send penalty too
        }),
      });

      const text = await res.text();
      if (!res.ok) {
        throw new Error(text || `Error: ${res.status}`);
      }

      let data = {};
      if (text) {
        try {
          data = JSON.parse(text);
        } catch {
          data = {};
        }
      }

      // Update local state
      setCurrentPosts((prev) =>
        prev.map((row) =>
          row.taskid === selectedTaskId
            ? {
                ...row,
                quality_grade: data.quality_grade ?? gradeNum,
                taskmanager: data.taskmanager ?? taskManagerName,
                recommendation: data.recommendation ?? recommendation,
                penalty: data.penalty ?? penaltyNum,
              }
            : row
        )
      );

      toast.success("✅ Task updated successfully!");
      setEditMode(false);
      setSelectedTaskId(null);
      setQualityGrade("");
      setRecommendation("");
      setPenalty("");
    } catch (error) {
      toast.error(`❌ ${error.message || "Failed to update task."}`);
    }
  };

  const columns = [
    { key: "id", label: "ID", align: "center", bold: true },
    { key: "taskid", label: "Task ID" },
    { key: "seq_no", label: "Seq No" },
    { key: "projectid", label: "Project ID" },
    { key: "userid", label: "User" },
    { key: "status", label: "Status" },
    { key: "activity", label: "Activity" },
    { key: "quality_grade", label: "Quality Grade" },
    { key: "penalty", label: "Penalty" },
  ];

  return (
    <div className="table-responsive" style={{ overflowX: "auto" }}>
      <table
        className="table table-bordered"
        ref={tableRef}
        style={{ tableLayout: "fixed" }}
      >
        <thead>
          <tr>
            <th className="text-center" style={{ width: 30 }}>
              <input
                type="checkbox"
                checked={
                  currentPosts.length > 0 &&
                  selectedIds.length === currentPosts.length
                }
                onChange={handleSelectAll}
              />
            </th>
            {columns.map((col, idx) => (
              <th
                key={col.key}
                className={`text-${col.align || "left"} sortable`}
                style={{
                  width: colWidths[col.key] ?? 80,
                  cursor: "pointer",
                  position: "relative",
                  userSelect: "none",
                }}
                onClick={() => handleSort(col.key)}
              >
                <div className="d-flex align-items-center justify-content-center gap-1">
                  <BsArrowDownUp size={14} />
                  <span className="fw-bold">{col.label}</span>
                </div>
                {idx < columns.length - 1 && (
                  <div
                    onMouseDown={(e) => onMouseDown(e, col.key)}
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 0,
                      height: "100%",
                      width: 6,
                      cursor: "col-resize",
                      userSelect: "none",
                      zIndex: 10,
                    }}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row) => (
            <tr
              key={row.id}
              style={{
                backgroundColor: selectedIds.includes(row.id)
                  ? "#c1d3f0"
                  : "",
              }}
            >
              <td className="text-center" style={{ width: 10 }}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(row.id)}
                  onChange={() => handleCheckboxChange(row.id)}
                />
              </td>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`wrap-text-style text-${
                    col.align || "left"
                  } ${col.bold ? "fw-bold" : ""}`}
                  style={{
                    width: colWidths[col.key],
                    maxWidth: colWidths[col.key],
                  }}
                >
                  {col.key === "taskid" ? (
                    <button
                      className="btn btn-link p-0 m-0"
                      style={{
                        textDecoration: "underline",
                        color: "#007bff",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (!row.taskid) {
                          toast.error("Task ID is missing");
                          return;
                        }
                        setSelectedTaskId(row.taskid);
                      }}
                    >
                      {row.taskid}
                    </button>
                  ) : (
                    (() => {
                      let displayValue = row[col.key];
                      if (col.key === "projectid") {
                        displayValue = projectMap[row.projectid] ?? row.projectid;
                      } else if (col.key === "userid") {
                        displayValue = userMap[row.userid] ?? row.userid;
                      }
                      return String(displayValue ?? "-");
                    })()
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {selectedTask && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1200 }}
          onClick={() => setSelectedTaskId(null)}
        >
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <Draggable handle=".modal-header">
              <div className="modal-content">
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h5 className="modal-title">Activity Details — {selectedTask.taskid}</h5>
                  <div>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setEditMode((prev) => !prev)}
                    >
                      {editMode ? "Cancel" : "Edit"}
                    </button>
                    <button
                      type="button"
                      className="btn-close ms-2"
                      onClick={() => setSelectedTaskId(null)}
                    />
                  </div>
                </div>

                <div
                  className="modal-body"
                  style={{
                    maxHeight: "400px",
                    overflowY: "auto",
                    textAlign: "left",
                  }}
                >
                  <table className="table table-bordered">
                    <tbody>
                      {allowedFields.map((key) => {
                        if (key === "project_name" || key === "user_name") return null;

                        let value = selectedTask[key];
                        if (key === "projectid") {
                          value = projectMap[value] ?? value;
                        } else if (key === "userid") {
                          value = userMap[value] ?? value;
                        }

                        // For editing quality_grade
                        if (editMode && key === "quality_grade") {
                          return (
                            <tr key={key}>
                              <th
                                style={{
                                  width: "40%",
                                  backgroundColor: "#f8f9fa",
                                  textTransform: "capitalize",
                                  textAlign: "left",
                                }}
                              >
                                {key.replace(/_/g, " ")}
                              </th>
                              <td style={{ width: "60%", textAlign: "left" }}>
                                <select
                                  value={qualityGrade}
                                  onChange={(e) => setQualityGrade(e.target.value)}
                                  className="form-select"
                                  style={{ width: "100px" }}
                                >
                                  <option value="">Select</option>
                                  {[1, 2, 3, 4, 5].map((num) => (
                                    <option key={num} value={num}>
                                      {num}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        }

                        // For editing penalty
                        if (editMode && key === "penalty") {
                          return (
                            <tr key={key}>
                              <th
                                style={{
                                  width: "40%",
                                  backgroundColor: "#f8f9fa",
                                  textTransform: "capitalize",
                                  textAlign: "left",
                                }}
                              >
                                {key.replace(/_/g, " ")}
                              </th>
                              <td style={{ width: "60%", textAlign: "left" }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control"
                                  value={penalty}
                                  onChange={(e) => setPenalty(e.target.value)}
                                />
                              </td>
                            </tr>
                          );
                        }

                        // Skip showing recommendation in this loop
                        if (key === "recommendation") return null;

                        return (
                          <tr key={key}>
                            <th
                              style={{
                                width: "40%",
                                backgroundColor: "#f8f9fa",
                                textTransform: "capitalize",
                                textAlign: "left",
                              }}
                            >
                              {key.replace(/_/g, " ")}
                            </th>
                            <td style={{ width: "60%", textAlign: "left" }}>
                              <span>
                                {value === null || value === undefined
                                  ? "-"
                                  : typeof value === "object"
                                  ? JSON.stringify(value, null, 2)
                                  : String(value)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}

                      <tr>
                        <th
                          style={{
                            width: "40%",
                            backgroundColor: "#f8f9fa",
                            textTransform: "capitalize",
                            textAlign: "left",
                          }}
                        >
                          Recommendation
                        </th>
                        <td style={{ width: "60%", textAlign: "left" }}>
                          {editMode ? (
                            <textarea
                              value={recommendation}
                              onChange={(e) => setRecommendation(e.target.value)}
                              className="form-control"
                              rows={4}
                              style={{ resize: "vertical" }}
                            />
                          ) : (
                            <pre
                              style={{
                                whiteSpace: "pre-wrap",
                                wordWrap: "break-word",
                                margin: 0,
                                fontFamily: "inherit",
                              }}
                            >
                              {recommendation || "-"}
                            </pre>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="modal-footer">
                  {editMode && (
                    <button className="btn btn-primary" onClick={handleUpdate}>
                      Save
                    </button>
                  )}
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedTaskId(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </Draggable>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Table;
